import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Shield, Calendar, Bell, Plus, LogOut, CheckCircle, CreditCard, Clock, Settings, Trash2, History, Users, AlertTriangle, X, Activity, User, Building2, FileText, ClipboardList } from 'lucide-react';
import AddPermitModal from '../components/AddPermitModal';
import PermitCard from '../components/PermitCard';
import AccountSettings from '../components/AccountSettings';
import CompanyEmployees from '../components/CompanyEmployees';
import ChangeRequestsPanel from '../components/ChangeRequestsPanel';
import './Dashboard.css';

const Dashboard = ({ setIsAuthenticated }) => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [permits, setPermits] = useState([]);
  const [pastPermits, setPastPermits] = useState([]);
  const [showPastPermits, setShowPastPermits] = useState(false);
  const [permitLimit, setPermitLimit] = useState(null);
  const [showAddPermit, setShowAddPermit] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showUpcomingQuals, setShowUpcomingQuals] = useState(false);
  const [showTotalPermitsModal, setShowTotalPermitsModal] = useState(false);
  const [showActivePermitsModal, setShowActivePermitsModal] = useState(false);
  const [showExpiredPermitsModal, setShowExpiredPermitsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [selectedPermits, setSelectedPermits] = useState([]);
  const [deletingPermits, setDeletingPermits] = useState(false);
  const [activeTab, setActiveTab] = useState('permits'); // 'permits' or 'employees'
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null); // For viewing specific employee's permits
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [showChangeRequests, setShowChangeRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  // Compute unique employees from permits (for company accounts) - sorted alphabetically
  const uniqueEmployees = React.useMemo(() => {
    if (!permits || permits.length === 0) return [];
    const employeeMap = new Map();
    permits.forEach(permit => {
      const empName = permit.employee_name?.trim();
      if (empName) {
        if (!employeeMap.has(empName.toLowerCase())) {
          employeeMap.set(empName.toLowerCase(), {
            name: empName,
            permits: [],
            permitCount: 0
          });
        }
        employeeMap.get(empName.toLowerCase()).permits.push(permit);
        employeeMap.get(empName.toLowerCase()).permitCount++;
      }
    });
    // Sort alphabetically by name
    return Array.from(employeeMap.values()).sort((a, b) => 
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
  }, [permits]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }

    try {
      const response = await axiosInstance.get(`/subscription/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Payment successful! Your subscription is now active.');
        // Refresh user data to get updated subscription status
        const userRes = await axiosInstance.get('/auth/me');
        setUser(userRes.data);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
      toast.error('Error checking payment status.');
    }
  }, []);

  useEffect(() => {
    // Check for payment callback
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'canceled') {
      toast.info('Payment was canceled.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [pollPaymentStatus]);

  // Handle browser back button - always go to home page
  useEffect(() => {
    // Push a new state so we can detect when user tries to go back
    window.history.pushState({ page: 'dashboard' }, '', window.location.pathname);
    
    const handlePopState = (event) => {
      // When back button is pressed, redirect to home page
      window.location.href = '/';
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, statsRes, permitsRes, limitRes, pastPermitsRes] = await Promise.all([
        axiosInstance.get('/auth/me'),
        axiosInstance.get('/dashboard/stats'),
        axiosInstance.get('/permits'),
        axiosInstance.get('/permits/limit'),
        axiosInstance.get('/permits/past').catch(() => ({ data: [] })), // Past permits may not exist yet
      ]);

      setUser(userRes.data);
      setStats(statsRes.data);
      setPermits(permitsRes.data);
      setPermitLimit(limitRes.data);
      setPastPermits(pastPermitsRes.data);
      
      // Fetch pending change requests count for company accounts
      if (userRes.data.account_type === 'company') {
        try {
          const countRes = await axiosInstance.get('/company/change-requests/count');
          setPendingRequestsCount(countRes.data.pending_count || 0);
        } catch (e) {
          console.log('Could not fetch change request count');
        }
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const fetchActivityLog = async () => {
    setLoadingActivity(true);
    try {
      const endpoint = user?.account_type === 'company' ? '/company/activity' : '/activity';
      const response = await axiosInstance.get(`${endpoint}?limit=50`);
      setActivityLog(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handlePermitAdded = () => {
    fetchData();
    setShowAddPermit(false);
    toast.success('Permit added successfully');
  };

  const handleAddPermitClick = () => {
    if (!user?.is_subscription_active) {
      toast.error('Please subscribe to add permits. Your free trial has expired.');
      return;
    }
    
    // Check permit limit for company accounts
    if (permitLimit && !permitLimit.allowed) {
      if (permitLimit.upgrade_available) {
        setShowUpgradeModal(true);
      } else {
        toast.error(`You have reached your maximum limit of ${permitLimit.limit} permits.`);
      }
      return;
    }
    
    setShowAddPermit(true);
  };

  const handleUpgrade = async () => {
    try {
      const response = await axiosInstance.post('/subscription/checkout', {
        origin_url: window.location.origin,
        plan_type: 'company',
        billing_interval: 'yearly',
        company_size: 'large'
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to start upgrade. Please try again.');
      console.error('Upgrade error:', error);
    }
  };

  const handleSubscribe = async (billingInterval = 'monthly', companySize = 'small') => {
    try {
      // Use correct plan type based on user's account type
      const planType = user?.account_type === 'company' ? 'company' : 'individual';
      
      const response = await axiosInstance.post('/subscription/checkout', {
        origin_url: window.location.origin,
        plan_type: planType,
        billing_interval: billingInterval,
        company_size: companySize
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      console.error('Checkout error:', error);
    }
  };

  const handleShowSubscriptionOptions = () => {
    setShowSubscriptionModal(true);
  };

  // Permit selection handlers for batch delete
  const handleSelectPermit = (permitId) => {
    setSelectedPermits((prev) =>
      prev.includes(permitId)
        ? prev.filter((id) => id !== permitId)
        : [...prev, permitId]
    );
  };

  const handleSelectAllPermits = () => {
    if (selectedPermits.length === permits.length) {
      setSelectedPermits([]);
    } else {
      setSelectedPermits(permits.map((permit) => permit.id));
    }
  };

  const handleDeleteSelectedPermits = async () => {
    if (selectedPermits.length === 0) {
      toast.error('Please select permits to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedPermits.length} permit(s)? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeletingPermits(true);
    try {
      // Delete permits one by one
      for (const permitId of selectedPermits) {
        await axiosInstance.delete(`/permits/${permitId}`);
      }
      toast.success(`Successfully deleted ${selectedPermits.length} permit(s)`);
      setSelectedPermits([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some permits');
    } finally {
      setDeletingPermits(false);
    }
  };

  const getSubscriptionPrice = () => {
    if (user?.account_type === 'company') {
      return '$129.99/year (1-50) or $229.99/year (51-500)';
    }
    return '$2.99/month or $29.99/year';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPermitTypeName = (type) => {
    const names = {
      firearms: 'BSIS Exposed Firearm Permit',
      guard_card: 'BSIS Guard Card',
      baton: 'BSIS Baton Permit',
      bsis_ppo: 'BSIS PPO License',
      ccw_ca: 'California CCW Permit',
      ccw_out_of_state: 'Out of State CCW Permit',
      hr218: 'HR218 LEOSA Permit',
      other: 'Custom Permit',
    };
    return names[type] || type;
  };

  const getSubscriptionBadge = () => {
    if (!user) return null;
    
    if (user.is_admin) {
      return (
        <span className="subscription-badge admin">
          Admin
        </span>
      );
    }
    
    if (user.subscription_status === 'active') {
      return (
        <span className="subscription-badge active">
          <CheckCircle size={14} />
          Subscribed
        </span>
      );
    }
    
    if (user.subscription_status === 'trial') {
      return (
        <span className="subscription-badge trial">
          <Clock size={14} />
          Trial: {user.days_remaining} days left
        </span>
      );
    }
    
    return (
      <span className="subscription-badge expired">
        <Bell size={14} />
        Trial Expired
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="dashboard">
      <nav className="dashboard-nav">
        <div className="nav-content">
          <div className="nav-left">
            <img src="/stc-logo.png" alt="STC" className="nav-logo-img" />
            <span className="nav-title">Permit Tracker</span>
          </div>
          <div className="nav-right">
            {getSubscriptionBadge()}
            <button
              className="btn-icon settings-btn"
              onClick={() => setShowAccountSettings(true)}
              title="Account Settings"
              data-testid="account-settings-btn"
            >
              <Settings size={20} />
            </button>
            <span className="user-name" data-testid="user-name">{user?.full_name}</span>
            <button
              className="btn-outline logout-btn"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="dashboard-content">
        {/* Subscription Banner */}
        {user && !user.is_admin && !user.is_subscription_active && (
          <div className="subscription-banner expired-banner" data-testid="subscription-banner">
            <div className="banner-content">
              <div className="banner-text">
                <h3>Your Free Trial Has Expired</h3>
                <p>Subscribe for {getSubscriptionPrice()} to continue tracking your permits</p>
              </div>
              <button className="btn-primary subscribe-btn" onClick={handleShowSubscriptionOptions}>
                <CreditCard size={18} />
                Subscribe Now
              </button>
            </div>
          </div>
        )}

        {user && user.subscription_status === 'trial' && user.days_remaining <= 3 && (
          <div className="subscription-banner trial-warning" data-testid="trial-warning">
            <div className="banner-content">
              <div className="banner-text">
                <h3>Your Trial Ends Soon!</h3>
                <p>Only {user.days_remaining} day{user.days_remaining !== 1 ? 's' : ''} left. Subscribe to keep tracking your permits.</p>
              </div>
              <button className="btn-primary subscribe-btn" onClick={handleShowSubscriptionOptions}>
                <CreditCard size={18} />
                View Plans
              </button>
            </div>
          </div>
        )}

        {user && user.subscription_status === 'expired' && user.days_until_deletion !== null && (
          <div className="subscription-banner deletion-warning-banner" data-testid="deletion-warning">
            <div className="banner-content">
              <div className="banner-text">
                <h3>⚠️ Account Scheduled for Deletion</h3>
                <p>Your account and all data will be permanently deleted in <strong>{user.days_until_deletion} day{user.days_until_deletion !== 1 ? 's' : ''}</strong>. Subscribe now to keep your data!</p>
              </div>
              <button className="btn-primary subscribe-btn" onClick={handleShowSubscriptionOptions}>
                <CreditCard size={18} />
                Subscribe Now
              </button>
            </div>
          </div>
        )}

        {checkingPayment && (
          <div className="subscription-banner processing-banner">
            <div className="banner-content">
              <div className="banner-text">
                <h3>Processing Payment...</h3>
                <p>Please wait while we confirm your subscription.</p>
              </div>
              <div className="spinner small"></div>
            </div>
          </div>
        )}

        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title" data-testid="dashboard-title">
              {user?.is_company_employee ? `${user?.company_name || 'Company'} Permit Tracker` : 'Dashboard'}
            </h1>
            <p className="dashboard-subtitle">
              {user?.is_company_employee 
                ? 'View your permits and request changes' 
                : 'Manage your permits and certifications'}
            </p>
          </div>
          <div className="dashboard-header-actions">
            {activeTab === 'permits' && !user?.is_company_employee && (
              <button
                className={`btn-action-primary ${!user?.is_subscription_active ? 'disabled' : ''}`}
                onClick={handleAddPermitClick}
                data-testid="add-permit-btn"
              >
                <Plus size={20} />
                {user?.account_type === 'company' ? 'ADD NEW PERSON TO TRACK' : 'Add Permit'}
              </button>
            )}
            {user?.account_type === 'company' && !user?.is_company_employee && (
              <button
                className="btn-action-secondary"
                onClick={() => setActiveTab('employees')}
                data-testid="manage-employees-btn"
              >
                <Users size={20} />
                MANAGE EMPLOYEES
              </button>
            )}
            {user?.account_type === 'company' && !user?.is_company_employee && (
              <button
                className="btn-action-secondary btn-activity"
                onClick={() => { setShowActivityModal(true); fetchActivityLog(); }}
                data-testid="activity-btn"
              >
                <Activity size={20} />
                ACTIVITY
              </button>
            )}
            {user?.account_type === 'company' && !user?.is_company_employee && (
              <button
                className="btn-action-secondary btn-change-requests"
                onClick={() => setShowChangeRequests(true)}
                data-testid="change-requests-btn"
              >
                <ClipboardList size={20} />
                APPROVALS
                {pendingRequestsCount > 0 && (
                  <span className="badge-count">{pendingRequestsCount}</span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Tabs for Company Accounts */}
        {user?.account_type === 'company' && (
          <div className="dashboard-tabs">
            <button 
              className={`tab-btn ${activeTab === 'permits' ? 'active' : ''}`}
              onClick={() => setActiveTab('permits')}
            >
              <Shield size={18} />
              Permits
            </button>
            <button 
              className={`tab-btn ${activeTab === 'employees' ? 'active' : ''}`}
              onClick={() => setActiveTab('employees')}
            >
              <Users size={18} />
              Employees
            </button>
          </div>
        )}

        {activeTab === 'permits' && (
          <>
            <div className="stats-grid">
              <div 
                className="stat-card clickable" 
                data-testid="stat-total-permits"
                onClick={() => (stats?.total_permits || 0) > 0 && setShowTotalPermitsModal(true)}
                title={(stats?.total_permits || 0) > 0 ? "Click to view all permits" : "No permits"}
                style={{ cursor: (stats?.total_permits || 0) > 0 ? 'pointer' : 'default' }}
              >
                <div className="stat-icon" style={{ backgroundColor: '#DBEAFE' }}>
                  <Shield size={24} style={{ color: '#1E40AF' }} />
                </div>
                <div className="stat-content">
                  <p className="stat-label">Total Permits</p>
                  <p className="stat-value">{stats?.total_permits || 0}</p>
                  {(stats?.total_permits || 0) > 0 && (
                    <p className="stat-hint">Click to view details</p>
                  )}
                </div>
              </div>

              {/* Show Total Employees for Company accounts, Active Permits for Individual */}
              {user?.account_type === 'company' ? (
                <div 
                  className="stat-card clickable" 
                  data-testid="stat-total-employees"
                  onClick={() => uniqueEmployees.length > 0 && setShowEmployeesModal(true)}
                  title={uniqueEmployees.length > 0 ? "Click to view all employees" : "No employees yet"}
                  style={{ cursor: uniqueEmployees.length > 0 ? 'pointer' : 'default' }}
                >
                  <div className="stat-icon" style={{ backgroundColor: '#E0E7FF' }}>
                    <Users size={24} style={{ color: '#4338CA' }} />
                  </div>
                  <div className="stat-content">
                    <p className="stat-label">Total Employees</p>
                    <p className="stat-value">{uniqueEmployees.length}</p>
                    {uniqueEmployees.length > 0 && (
                      <p className="stat-hint">Click to view details</p>
                    )}
                  </div>
                </div>
              ) : (
                <div 
                  className="stat-card clickable" 
                  data-testid="stat-active-permits"
                  onClick={() => (stats?.active_permits || 0) > 0 && setShowActivePermitsModal(true)}
                  title={(stats?.active_permits || 0) > 0 ? "Click to view active permits" : "No active permits"}
                  style={{ cursor: (stats?.active_permits || 0) > 0 ? 'pointer' : 'default' }}
                >
                  <div className="stat-icon" style={{ backgroundColor: '#D1FAE5' }}>
                    <CheckCircle size={24} style={{ color: '#065F46' }} />
                  </div>
                  <div className="stat-content">
                    <p className="stat-label">Active Permits</p>
                    <p className="stat-value">{stats?.active_permits || 0}</p>
                    {(stats?.active_permits || 0) > 0 && (
                      <p className="stat-hint">Click to view details</p>
                    )}
                  </div>
                </div>
              )}

              <div 
                className="stat-card clickable" 
                data-testid="stat-expired-permits"
                onClick={() => (stats?.expired_permits || 0) > 0 && setShowExpiredPermitsModal(true)}
                title={(stats?.expired_permits || 0) > 0 ? "Click to view expired permits" : "No expired permits"}
                style={{ cursor: (stats?.expired_permits || 0) > 0 ? 'pointer' : 'default' }}
              >
                <div className="stat-icon" style={{ backgroundColor: '#FEE2E2' }}>
                  <Calendar size={24} style={{ color: '#991B1B' }} />
                </div>
                <div className="stat-content">
                  <p className="stat-label">Expired Permits</p>
                  <p className="stat-value">{stats?.expired_permits || 0}</p>
                  {(stats?.expired_permits || 0) > 0 && (
                    <p className="stat-hint">Click to view details</p>
                  )}
                </div>
              </div>

              <div 
                className="stat-card clickable" 
                data-testid="stat-upcoming-quals"
                onClick={() => stats?.upcoming_qualifications?.length > 0 && setShowUpcomingQuals(true)}
                title={stats?.upcoming_qualifications?.length > 0 ? "Click to view all upcoming qualifications" : "No upcoming qualifications"}
                style={{ cursor: stats?.upcoming_qualifications?.length > 0 ? 'pointer' : 'default' }}
              >
                <div className="stat-icon" style={{ backgroundColor: '#FEF3C7' }}>
                  <Bell size={24} style={{ color: '#92400E' }} />
                </div>
                <div className="stat-content">
                  <p className="stat-label">Upcoming Qualifications</p>
                  <p className="stat-value">{stats?.upcoming_qualifications?.length || 0}</p>
                  {stats?.upcoming_qualifications?.length > 0 && (
                    <p className="stat-hint">Click to view details</p>
                  )}
                </div>
              </div>
            </div>

        {/* Employee Limit Indicator for Company Accounts - NOT Permit limit */}
        {user?.account_type === 'company' && (
          <div className="employee-limit-banner">
            <div className="limit-info">
              <span className="limit-label">Employee Slots:</span>
              <span className="limit-count">
                {stats?.employee_count || 0} / {stats?.employee_limit || 500}
              </span>
              {stats?.employee_limit && stats?.employee_count >= stats?.employee_limit - 10 && (
                <span className="limit-warning">Running low on employee slots!</span>
              )}
            </div>
          </div>
        )}

        {stats?.upcoming_qualifications && stats.upcoming_qualifications.length > 0 && (
          <div className="upcoming-section">
            <h2 className="section-title">Next Qualification Due</h2>
            <div className="next-qual-card" data-testid="next-qual-card">
              <div className="next-qual-content">
                <div className="next-qual-date">
                  <span className="date-day">
                    {new Date(stats.upcoming_qualifications[0].due_date).getDate()}
                  </span>
                  <span className="date-month">
                    {new Date(stats.upcoming_qualifications[0].due_date).toLocaleDateString(
                      'en-US',
                      { month: 'short', year: 'numeric' }
                    )}
                  </span>
                </div>
                <div className="next-qual-info">
                  {stats.upcoming_qualifications[0].employee_name && (
                    <p className="next-qual-employee">{stats.upcoming_qualifications[0].employee_name}</p>
                  )}
                  <p className="next-qual-type">
                    {stats.upcoming_qualifications[0].qualification_name || 'BSIS Firearm Qualification'}
                  </p>
                  <p className="next-qual-label">Days Until Qualification</p>
                  <p className="next-qual-days mono">
                    {stats.upcoming_qualifications[0].days_until} days
                  </p>
                  <p className="next-qual-text">
                    Schedule your qualification at Southwest Training Center
                  </p>
                  <p className="next-qual-contact">Call 310-650-6896</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* For Company Accounts: Show Employees Section */}
        {user?.account_type === 'company' && uniqueEmployees.length > 0 && !selectedEmployee && (
          <div className="permits-section employees-section">
            <div className="permits-header">
              <h2 className="section-title">Your Employees</h2>
              <span className="employee-count-badge">{uniqueEmployees.length} employee{uniqueEmployees.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="employees-stacked-list">
              {uniqueEmployees.map((employee, index) => (
                <div 
                  key={index} 
                  className="employee-row"
                  onClick={() => setSelectedEmployee(employee)}
                >
                  <span className="employee-row-name">{employee.name}</span>
                  <span className="employee-row-permits">{employee.permitCount} permit{employee.permitCount !== 1 ? 's' : ''}</span>
                  <span className="employee-row-arrow">→</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Employee's Permits View */}
        {user?.account_type === 'company' && selectedEmployee && (
          <div className="permits-section">
            <div className="permits-header">
              <button className="back-btn" onClick={() => setSelectedEmployee(null)}>
                ← Back to Employees
              </button>
              <h2 className="section-title">Permits for {selectedEmployee.name}</h2>
              <button
                className={`btn-action-primary ${!user?.is_subscription_active ? 'disabled' : ''}`}
                onClick={handleAddPermitClick}
              >
                <Plus size={18} />
                Add Permit
              </button>
            </div>
            <div className="permits-grid">
              {selectedEmployee.permits.map((permit) => (
                <div key={permit.id} className="permit-wrapper">
                  <PermitCard
                    permit={permit}
                    onUpdate={fetchData}
                    isCompanyAccount={true}
                    isEmployee={false}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Original Permits Section - Show for Individual accounts or Company with no employees */}
        {(user?.account_type !== 'company' || (user?.account_type === 'company' && uniqueEmployees.length === 0)) && (
        <div className="permits-section">
          <div className="permits-header">
            <h2 className="section-title">Your Permits</h2>
            {permits.length > 0 && !user?.is_company_employee && (
              <div className="permits-actions">
                <label className="select-all-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedPermits.length === permits.length && permits.length > 0}
                    onChange={handleSelectAllPermits}
                  />
                  <span>Select All</span>
                </label>
                {selectedPermits.length > 0 && (
                  <button
                    className="btn-danger delete-selected-btn"
                    onClick={handleDeleteSelectedPermits}
                    disabled={deletingPermits}
                  >
                    <Trash2 size={18} />
                    {deletingPermits ? 'Deleting...' : `Delete Selected (${selectedPermits.length})`}
                  </button>
                )}
              </div>
            )}
          </div>
          {permits.length === 0 ? (
            <div className="empty-state" data-testid="empty-state">
              <Shield size={64} style={{ color: '#CBD5E1' }} />
              <h3>No Permits Yet</h3>
              <p>{user?.account_type === 'company' 
                ? 'Add permits for your employees to start tracking' 
                : 'Add your first permit to get started'}</p>
              <button
                className={`btn-primary ${!user?.is_subscription_active ? 'disabled' : ''}`}
                onClick={handleAddPermitClick}
                data-testid="empty-state-add-btn"
              >
                <Plus size={20} />
                {user?.account_type === 'company' ? 'ADD NEW PERSON TO TRACK' : 'Add Your First Permit'}
              </button>
              {user?.account_type === 'company' && (
                <p className="empty-state-hint">
                  You can also <button className="link-btn" onClick={() => setActiveTab('employees')}>manage your employees</button> in the Employees tab
                </p>
              )}
            </div>
          ) : (
            <div className="permits-grid">
              {permits.map((permit) => (
                <div key={permit.id} className="permit-wrapper">
                  {!user?.is_company_employee && (
                    <div className="permit-select-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPermits.includes(permit.id)}
                        onChange={() => handleSelectPermit(permit.id)}
                      />
                    </div>
                  )}
                  <PermitCard 
                    permit={permit} 
                    onUpdate={fetchData} 
                    isCompanyAccount={user?.account_type === 'company'}
                    isEmployee={user?.is_company_employee}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Past Permits Section */}
        {pastPermits.length > 0 && (
          <div className="past-permits-section">
            <div className="past-permits-header" onClick={() => setShowPastPermits(!showPastPermits)}>
              <div className="past-permits-title-row">
                <History size={20} />
                <h2 className="section-title">Past Permits ({pastPermits.length})</h2>
              </div>
              <button className="view-history-btn">
                {showPastPermits ? 'Hide History' : 'View History'}
              </button>
            </div>
            {showPastPermits && (
              <div className="past-permits-grid">
                {pastPermits.map((permit) => (
                  <div key={permit.id} className="past-permit-card">
                    <div className="past-permit-header">
                      <span className="past-permit-type">
                        {permit.custom_permit_name || getPermitTypeName(permit.permit_type)}
                      </span>
                      <span className="past-permit-badge">ARCHIVED</span>
                    </div>
                    <div className="past-permit-info">
                      <p><strong>Permit #:</strong> {permit.permit_number}</p>
                      <p><strong>Period:</strong> {formatDate(permit.issue_date)} — {formatDate(permit.expiration_date)}</p>
                      {permit.archived_at && (
                        <p className="archived-date"><strong>Archived:</strong> {formatDate(permit.archived_at)}</p>
                      )}
                    </div>
                    <div className="past-permit-quals">
                      <p className="quals-label">Qualifications Completed:</p>
                      <div className="quals-status">
                        <span className={`qual-dot ${permit.qual_1?.completed ? 'completed' : ''}`}>Q1</span>
                        <span className={`qual-dot ${permit.qual_2?.completed ? 'completed' : ''}`}>Q2</span>
                        <span className={`qual-dot ${permit.qual_3?.completed ? 'completed' : ''}`}>Q3</span>
                        <span className={`qual-dot ${permit.qual_4_renewal?.completed ? 'completed' : ''}`}>Q4</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}

        {/* Employees Tab Content */}
        {activeTab === 'employees' && user?.account_type === 'company' && (
          <CompanyEmployees companyUser={user} />
        )}
      </div>

      {showAddPermit && (
        <AddPermitModal
          onClose={() => setShowAddPermit(false)}
          onSuccess={handlePermitAdded}
        />
      )}

      <AccountSettings
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        user={user}
        onLogout={() => {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        }}
      />

      {/* Subscription Pricing Modal */}
      {showSubscriptionModal && (
        <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
          <div className="modal subscription-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Choose Your Plan</h2>
              <button className="close-btn" onClick={() => setShowSubscriptionModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {user?.account_type === 'individual' ? (
                <div className="pricing-grid">
                  <div className="pricing-card">
                    <h3>Monthly</h3>
                    <div className="price">
                      <span className="amount">$2.99</span>
                      <span className="period">/month</span>
                    </div>
                    <ul className="features">
                      <li>✓ Track unlimited permits</li>
                      <li>✓ Email & SMS reminders</li>
                      <li>✓ Qualification tracking</li>
                      <li>✓ Cancel anytime</li>
                    </ul>
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        setShowSubscriptionModal(false);
                        handleSubscribe('monthly');
                      }}
                    >
                      Subscribe Monthly
                    </button>
                  </div>
                  <div className="pricing-card featured">
                    <div className="badge">Best Value</div>
                    <h3>Yearly</h3>
                    <div className="price">
                      <span className="amount">$29.99</span>
                      <span className="period">/year</span>
                    </div>
                    <p className="savings">Save $6 compared to monthly!</p>
                    <ul className="features">
                      <li>✓ Track unlimited permits</li>
                      <li>✓ Email & SMS reminders</li>
                      <li>✓ Qualification tracking</li>
                      <li>✓ Priority support</li>
                    </ul>
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        setShowSubscriptionModal(false);
                        handleSubscribe('yearly');
                      }}
                    >
                      Subscribe Yearly
                    </button>
                  </div>
                </div>
              ) : (
                <div className="pricing-grid company-pricing">
                  <div className="pricing-card">
                    <h3>Small Company</h3>
                    <p className="employee-range">1-50 Employees</p>
                    <div className="price">
                      <span className="amount">$129.99</span>
                      <span className="period">/year</span>
                    </div>
                    <ul className="features">
                      <li>✓ Manage permits for up to 50 employees</li>
                      <li>✓ Email & SMS reminders</li>
                      <li>✓ Qualification tracking</li>
                      <li>✓ Admin dashboard</li>
                    </ul>
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        setShowSubscriptionModal(false);
                        handleSubscribe('yearly', 'small');
                      }}
                    >
                      Select Plan
                    </button>
                  </div>
                  <div className="pricing-card featured">
                    <div className="badge">Large Teams</div>
                    <h3>Large Company</h3>
                    <p className="employee-range">51-500 Employees</p>
                    <div className="price">
                      <span className="amount">$229.99</span>
                      <span className="period">/year</span>
                    </div>
                    <ul className="features">
                      <li>✓ Manage permits for up to 500 employees</li>
                      <li>✓ Email & SMS reminders</li>
                      <li>✓ Qualification tracking</li>
                      <li>✓ Priority support</li>
                    </ul>
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        setShowSubscriptionModal(false);
                        handleSubscribe('yearly', 'large');
                      }}
                    >
                      Select Plan
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal for Company accounts at limit */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal upgrade-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upgrade Your Plan</h2>
              <button className="close-btn" onClick={() => setShowUpgradeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="upgrade-content">
                <div className="upgrade-icon">⚠️</div>
                <h3>You&apos;ve Reached Your Permit Limit</h3>
                <p>
                  Your current plan allows up to <strong>{permitLimit?.limit || 50} permits</strong>. 
                  You currently have <strong>{permitLimit?.current || 0} permits</strong>.
                </p>
                <p>Upgrade to the <strong>Large Company Plan (51-500 employees)</strong> to add more permits.</p>
                
                <div className="upgrade-pricing">
                  <div className="current-plan">
                    <span className="plan-label">Current Plan</span>
                    <span className="plan-name">Company (1-50)</span>
                    <span className="plan-price">$129.99/year</span>
                  </div>
                  <div className="arrow">→</div>
                  <div className="new-plan">
                    <span className="plan-label">Upgrade To</span>
                    <span className="plan-name">Company (51-500)</span>
                    <span className="plan-price">$229.99/year</span>
                  </div>
                </div>

                <button className="btn-primary upgrade-btn" onClick={handleUpgrade}>
                  <CreditCard size={18} />
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Qualifications Modal */}
      {/* Total Permits Modal */}
      {showTotalPermitsModal && (
        <div className="modal-overlay" onClick={() => setShowTotalPermitsModal(false)}>
          <div className="modal permits-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Shield size={20} />
                Total Permits ({stats?.total_permits || 0})
              </h2>
              <button className="close-btn" onClick={() => setShowTotalPermitsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="permits-summary-list">
                {permits.map((permit, index) => (
                  <div key={index} className="permit-summary-item">
                    <div className="permit-summary-header">
                      <span className="permit-summary-type">
                        {permit.permit_type === 'firearms' ? '🔫 BSIS Exposed Firearm' : 
                         permit.permit_type === 'guard_card' ? '🛡️ Guard Card' : 
                         permit.permit_type === 'ccw' ? '🔒 CCW' : permit.permit_type}
                      </span>
                      <span className={`permit-summary-status ${new Date(permit.expiration_date) < new Date() ? 'expired' : 'active'}`}>
                        {new Date(permit.expiration_date) < new Date() ? 'EXPIRED' : 'ACTIVE'}
                      </span>
                    </div>
                    {permit.employee_name && <span className="permit-summary-employee">👤 {permit.employee_name}</span>}
                    <div className="permit-summary-details">
                      <span>#{permit.permit_number}</span>
                      <span>Expires: {new Date(permit.expiration_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employees Modal (for Company accounts) */}
      {showEmployeesModal && (
        <div className="modal-overlay" onClick={() => setShowEmployeesModal(false)}>
          <div className="modal permits-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderLeft: '4px solid #4338CA' }}>
              <h2>
                <Users size={20} style={{ color: '#4338CA' }} />
                Employees ({uniqueEmployees.length})
              </h2>
              <button className="close-btn" onClick={() => setShowEmployeesModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="permits-summary-list">
                {uniqueEmployees.map((employee, index) => (
                  <div key={index} className="permit-summary-item employee-item">
                    <div className="permit-summary-header">
                      <span className="permit-summary-type">👤 {employee.name}</span>
                      <span className="permit-count-badge">{employee.permitCount} permit{employee.permitCount !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="employee-permits-list">
                      {employee.permits.map((permit, pIdx) => (
                        <div key={pIdx} className="employee-permit-item">
                          <span className="permit-type-small">
                            {permit.permit_type === 'firearms' ? '🔫' : 
                             permit.permit_type === 'guard_card' ? '🛡️' : 
                             permit.permit_type === 'ccw' ? '🔒' : '📄'} {permit.permit_type}
                          </span>
                          <span className={`permit-status-small ${new Date(permit.expiration_date) < new Date() ? 'expired' : 'active'}`}>
                            {new Date(permit.expiration_date) < new Date() ? 'Expired' : 'Active'}
                          </span>
                          <span className="permit-exp-small">
                            Exp: {new Date(permit.expiration_date).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Permits Modal */}
      {showActivePermitsModal && (
        <div className="modal-overlay" onClick={() => setShowActivePermitsModal(false)}>
          <div className="modal permits-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderLeft: '4px solid #10B981' }}>
              <h2>
                <CheckCircle size={20} style={{ color: '#10B981' }} />
                Active Permits ({stats?.active_permits || 0})
              </h2>
              <button className="close-btn" onClick={() => setShowActivePermitsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="permits-summary-list">
                {permits.filter(p => new Date(p.expiration_date) >= new Date()).map((permit, index) => (
                  <div key={index} className="permit-summary-item active">
                    <div className="permit-summary-header">
                      <span className="permit-summary-type">
                        {permit.permit_type === 'firearms' ? '🔫 BSIS Exposed Firearm' : 
                         permit.permit_type === 'guard_card' ? '🛡️ Guard Card' : 
                         permit.permit_type === 'ccw' ? '🔒 CCW' : permit.permit_type}
                      </span>
                      <span className="permit-summary-status active">ACTIVE</span>
                    </div>
                    {permit.employee_name && <span className="permit-summary-employee">👤 {permit.employee_name}</span>}
                    <div className="permit-summary-details">
                      <span>#{permit.permit_number}</span>
                      <span>Expires: {new Date(permit.expiration_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expired Permits Modal */}
      {showExpiredPermitsModal && (
        <div className="modal-overlay" onClick={() => setShowExpiredPermitsModal(false)}>
          <div className="modal permits-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderLeft: '4px solid #EF4444' }}>
              <h2>
                <Calendar size={20} style={{ color: '#EF4444' }} />
                Expired Permits ({stats?.expired_permits || 0})
              </h2>
              <button className="close-btn" onClick={() => setShowExpiredPermitsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-info-text">These permits have passed their expiration date and need renewal.</p>
              <div className="permits-summary-list">
                {permits.filter(p => new Date(p.expiration_date) < new Date()).map((permit, index) => (
                  <div key={index} className="permit-summary-item expired">
                    <div className="permit-summary-header">
                      <span className="permit-summary-type">
                        {permit.permit_type === 'firearms' ? '🔫 BSIS Exposed Firearm' : 
                         permit.permit_type === 'guard_card' ? '🛡️ Guard Card' : 
                         permit.permit_type === 'ccw' ? '🔒 CCW' : permit.permit_type}
                      </span>
                      <span className="permit-summary-status expired">EXPIRED</span>
                    </div>
                    {permit.employee_name && <span className="permit-summary-employee">👤 {permit.employee_name}</span>}
                    <div className="permit-summary-details">
                      <span>#{permit.permit_number}</span>
                      <span>Expired: {new Date(permit.expiration_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showUpcomingQuals && stats?.upcoming_qualifications && (
        <div className="modal-overlay" onClick={() => setShowUpcomingQuals(false)}>
          <div className="modal upcoming-quals-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Bell size={20} />
                Upcoming Qualifications
              </h2>
              <button className="close-btn" onClick={() => setShowUpcomingQuals(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Critical Warning for Firearms Permits */}
              <div className="critical-warning">
                <AlertTriangle size={24} />
                <div className="warning-content">
                  <strong>⚠️ IMPORTANT - BSIS EXPOSED FIREARM PERMITS</strong>
                  <p>
                    BSIS Exposed Firearm Permits <strong>CAN NOT GO PAST THE EXPIRATION DATE</strong>. 
                    There is <strong>NO GRACE PERIOD</strong> and you will need to start a 
                    <strong> BRAND NEW APPLICATION</strong> if the expiration date is missed.
                  </p>
                </div>
              </div>

              <div className="quals-list">
                {stats.upcoming_qualifications.map((qual, index) => (
                  <div key={index} className="qual-item">
                    <div className="qual-header">
                      {qual.employee_name && (
                        <span className="qual-employee-name">{qual.employee_name}</span>
                      )}
                      <span className="qual-permit">
                        {qual.permit_type === 'firearms' ? '🔫 BSIS Exposed Firearm' : qual.permit_type}
                      </span>
                      <span className="qual-number">#{qual.permit_number}</span>
                    </div>
                    <div className="qual-name-badge">
                      {qual.qualification_name || `Qualification ${qual.qualification_number || 1}`}
                    </div>
                    <div className="qual-details">
                      <div className="qual-window">
                        <span className="qual-label">Qualification Window:</span>
                        <span className="qual-dates">
                          {qual.window_start && new Date(qual.window_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {qual.window_start && qual.window_end && ' → '}
                          {qual.window_end && new Date(qual.window_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <div className="qual-due">
                        <span className="qual-label">Must Complete By:</span>
                        <span className="qual-deadline">
                          {new Date(qual.due_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className={`qual-days ${qual.days_until <= 30 ? 'urgent' : ''}`}>
                          ({qual.days_until} days remaining)
                        </span>
                      </div>
                      <div className="qual-warning">
                        <AlertTriangle size={14} />
                        <span>CAN NOT BE PAST THIS DATE</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="quals-footer">
                <p>📞 Schedule your qualification: <strong>310-650-6896</strong></p>
                <a 
                  href="https://app.acuityscheduling.com/schedule/a5dbb23d" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary schedule-btn"
                >
                  <Calendar size={18} />
                  Schedule Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {showActivityModal && (
        <div className="modal-overlay" onClick={() => setShowActivityModal(false)}>
          <div className="modal activity-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Activity size={22} /> Activity Log</h2>
              <button className="close-btn" onClick={() => setShowActivityModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {loadingActivity ? (
                <div className="loading-state">Loading activity...</div>
              ) : activityLog.length === 0 ? (
                <div className="empty-activity">
                  <Activity size={48} />
                  <h3>No activity yet</h3>
                  <p>Activity will appear here as changes are made</p>
                </div>
              ) : (
                <div className="activity-list">
                  {activityLog.map((activity, idx) => (
                    <div key={activity.id || idx} className="activity-item">
                      <div className="activity-icon">
                        {activity.action_type.includes('permit') && <FileText size={18} />}
                        {activity.action_type.includes('employee') && <User size={18} />}
                        {activity.action_type.includes('qualification') && <Calendar size={18} />}
                        {!activity.action_type.includes('permit') && 
                         !activity.action_type.includes('employee') && 
                         !activity.action_type.includes('qualification') && <Activity size={18} />}
                      </div>
                      <div className="activity-content">
                        <div className="activity-header">
                          <span className="activity-user">{activity.user_name}</span>
                          <span className={`activity-badge ${activity.action_type.includes('created') ? 'created' : activity.action_type.includes('updated') ? 'updated' : activity.action_type.includes('terminated') ? 'terminated' : 'other'}`}>
                            {activity.action_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="activity-description">{activity.action_description}</p>
                        <span className="activity-time">
                          <Clock size={12} />
                          {new Date(activity.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Requests Panel */}
      {showChangeRequests && (
        <ChangeRequestsPanel
          onClose={() => setShowChangeRequests(false)}
          onUpdate={fetchData}
        />
      )}
    </div>
  );
};

export default Dashboard;
